import { url } from "./settings";
import { Fetch, isNumber } from "../../utilities/misc";
import * as actions from "../actionTypes";
import { orderInfo, orderType } from "../reducers/order";
import moment from "moment";
import { cartItem, extrasCartItem, gift_value } from "../reducers/cart";
import { getTimeStartInfo } from "../../utilities/cleanerTime";
import { getCookie } from "../../utilities/cookieManager";

const getEditDetails = (
  details: orderType,
  onlyGift: boolean
): Partial<orderType> => {
  const exclude: (keyof orderType)[] = [
    "cleaner_id",
    "time_start",
    "random_cleaner",
    ...(onlyGift
      ? (["client_address", "latitude", "longitude"] as (keyof orderType)[])
      : [])
  ];

  const orderDetails = { ...details } as Partial<orderType>;

  for (const k in orderDetails) {
    if (Object.prototype.hasOwnProperty.call(orderDetails, k)) {
      const key = k as keyof orderType;
      if (exclude.indexOf(key) >= 0) {
        delete orderDetails[key];
      }
    }
  }
  return orderDetails;
};

export type c_item = Omit<cartItem, "item" | "type" | "parent_id">;
export type e_item = Omit<extrasCartItem, "item" | "type">;
export function parseIdQuan(params: { item: e_item; extra: true }): {
  id: number;
  quantity: number;
  parent_id: number;
};
export function parseIdQuan(params: { item: c_item; extra: false }): {
  id: number;
  quantity: number;
  parent_id: undefined;
  is_upsell: boolean;
};
export function parseIdQuan(
  params: { item: c_item; extra: false } | { item: e_item; extra: true }
):
  | { id: number; quantity: number; parent_id: number }
  | {
    id: number;
    quantity: number;
    parent_id?: undefined;
    is_upsell: boolean;
  } {
  const { item, extra } = params;
  return extra
    ? {
      id: item.id,
      quantity: item.quantity,
      parent_id: item.parent_id
    }
    : { id: item.id, quantity: item.quantity, is_upsell: item.is_upsell };
}

export type createOrderParams = {
  by_staff: boolean;
  orderDetails: orderType;
  orderInfo: orderInfo;
  gift_values: gift_value[];
  services: { id: number; quantity: number; is_upsell: boolean }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
};
export const cancelDirectOrder = async (token: string) => {
  return await Fetch(`${url.createOrder}/${token}/cancel`, {
    method: "PUT"
  });
};

export const createOrUpdateOrder = async (details: createOrderParams) => {
  const { orderDetails, services, extra_services, gift_values, orderInfo } =
    details;
  const onlyGift =
    extra_services.length + services.length === 0 && !!gift_values.length;

  const exclude: (keyof orderType)[] = [
    "random_cleaner",
    "info",
    "full_payment",
    "coupon_code",
    "eircode",
    "latitude",
    "longitude",
    ...(onlyGift
      ? ([
          "cleaner_id",
          "time_start"
        ] as (keyof orderType)[])
      : [])
  ];

  const missingFields = Object.keys(orderDetails).filter((key) => {
    const Key = key as keyof orderType;
    if (exclude.indexOf(Key) >= 0) {
      return false;
    } else {
      if (Key === "cleaner_id") {
        return !orderDetails[Key] && !orderDetails["random_cleaner"];
      } else {
        return !orderDetails[Key];
      }
    }
  });

  const incomplete = missingFields.length > 0 || !(gift_values.length + services.length);

  if (incomplete) {
    return;
  } else {
    if (orderInfo?.orderId && !orderInfo?.reschedule_token) {
      // EDIT ORDER
      const id = orderInfo?.orderId;
      return await Fetch(`${url.createOrder}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(getEditDetails(orderDetails, onlyGift))
      }).then(
        (res) => {
          const result: Promise<actions.order> = res.json();
          return result;
        },
        (error) => {
          throw error;
        }
      );
    } else {
      if (orderInfo?.reschedule_token) {
        await cancelDirectOrder(orderInfo.reschedule_token).catch(console.error);
      }

      const cookie = getCookie("cleanbee_secretary") || undefined;
      // CREATE ORDER
      const payload = {
        ...orderDetails,
        services,
        gift_values,
        extra_services,
        by_staff: !!cookie,
        cleanbee_secretary: cookie,
        ...(onlyGift
          ? {
            client_address: null
          }
          : {})
      };

      return await Fetch(url.createOrder, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      }).then(
        (res) => {
          const result: Promise<actions.order> = res.json();
          return result;
        },
        (error) => {
          throw error;
        }
      );
    }
  }
};

export type disabledDateParams = {
  date: Date | undefined;
  services: { id: number; quantity: number }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
  cleaner_id: number | undefined;
  latitude: number | undefined;
  longitude: number | undefined;
};
export const getDisabledDates = async (params: disabledDateParams) => {
  const { date, services, extra_services, latitude, longitude, cleaner_id } =
    params;
  const dateString = moment(date).format("YYYY-MM-DD");

  if (!isNumber(latitude) || !isNumber(longitude) || !date) {
    return;
  }

  return await Fetch(url.disabledDates, {
    method: "POST",
    body: JSON.stringify({
      selected_date: dateString,
      ...{ services, extra_services, latitude, longitude, cleaner_id }
    })
  })
    .then((res) => {
      const response = res.json() as Promise<{
        disabled_days: string[];
      }>;
      return response;
    })
    .catch((err) => {
      throw err;
    });
};

export type checkAddressParams = {
  address?: string;
  longitude: number | string | undefined;
  latitude: number | string | undefined;
  date?: Date;
  services: { id: number; quantity: number }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
};
export const checkAddress = async ({
  date,
  address,
  ...param
}: checkAddressParams) => {
  if (!address || !date) {
    return;
  }
  return await Fetch(url.checkAddress, {
    method: "POST",
    body: JSON.stringify({
      ...param,
      address,
      date: moment(date).format("YYYY-MM-DD")
    })
  })
    .then((res) => {
      const cleaners = res.json() as Promise<actions.cleaner[]>;

      return cleaners;
    })
    .catch((err) => {
      throw err;
    });
};

export const checkEmail = async (param: { email?: string }) => {
  if (!param.email) {
    return null;
  }
  return await Fetch(url.checkEmail, {
    method: "POST",
    body: JSON.stringify({ ...param })
  })
    .then((res) => {
      const response = res.json() as Promise<{ success: boolean }>;

      return response;
    })
    .catch((err) => {
      throw err;
    });
};

export const confirmOrder = async (details: { id: number }) => {
  const { id } = details;

  if (!id) {
    throw new Error(`No ID provided`);
  } else {
    return await Fetch(`${url.createOrder}/${id}/confirm`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({})
    })
      .then((res) => {
        return;
      })
      .catch((error) => {
        throw error;
      });
  }
};

export const getOrderDetails = async (details: {
  token: string | undefined;
  orderId: string | undefined;
}) => {
  const { orderId, token } = details;

  if (!token || !orderId) {
    throw new Error(`No ID provided`);
  } else {
    return await Fetch(`${url.orderDetails}/${orderId}/${token}`).then(
      (res) => {
        const result: Promise<actions.orderDetailsType> = res.json();
        return result;
      },
      (error) => {
        throw error;
      }
    );
  }
};

export const rescheduleOrder = async (details: {
  token: string | undefined;
  time_start: string | undefined;
  orderId: number;
}) => {
  const { orderId, time_start, token } = details;

  if (!token || !time_start || !orderId) {
    throw new Error(`No ID provided`);
  } else {
    return await Fetch(`${url.orderDetails}/${orderId}/reschedule`, {
      body: JSON.stringify({
        token,
        time_start
      }),
      method: "PUT"
    }).then(
      (res) => {
        const result: Promise<{ data: actions.order["order"] }> = res.json();
        return result;
      },
      (error) => {
        throw error;
      }
    );
  }
};

export const formatDate = (date: Date | string | null | undefined) => {
  try {
    if (date) {
      date = new Date(date);

      if (date.getTime() !== date.getTime()) {
        throw `Invalid date!`;
      }

      const dateString = getTimeStartInfo(date).date;

      return dateString;
    } else {
      return `----`;
    }
  } catch (err) {
    return `----`;
  }
};

export type getCleanerTimeParams = {
  cleanerId: number | null;
  date: Date;
  random_cleaner: boolean;
  latitude: number | undefined;
  longitude: number | undefined;
  services: { id: number; quantity: number }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
};
export const get_cleaner_time = async (details: getCleanerTimeParams) => {
  const {
    cleanerId,
    date,
    random_cleaner,
    latitude,
    longitude,
    services,
    extra_services
  } = details;

  if (
    !date ||
    (!cleanerId && !random_cleaner) ||
    !String(latitude) ||
    !String(longitude)
  ) {
    return;
  }

  const dateString = moment(date).format("YYYY-MM-DD");

  return await Fetch(`${url.getCleanerTime}/available-time/${dateString}`, {
    method: "POST",
    body: JSON.stringify({
      services,
      extra_services,
      latitude,
      longitude,
      cleanerId
    })
  })
    .then((res) => {
      const result: Promise<actions.timeCell[]> = res.json();
      return result;
    })
    .catch((err) => {
      throw err;
    });
};

export const getOrderPrice = async (details: {
  services: { id: number; quantity: number; parent_id: undefined }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
  gift_values: gift_value[];
  coupon_code: string | null;
  details?: orderType;
  order_id?: number;
  show_banner?: boolean;
}) => {
  const { coupon_code, services, gift_values } = details;
  const service_length = services.length + gift_values.length;
  if (!service_length || !coupon_code) {
    return;
  } else {
    return await Fetch(url.calculateOrderAmount, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(details)
    }).then(
      async (res) => {
        const result: number = await res.json();
        return { amount: result, coupon: coupon_code };
      },
      (error) => {
        throw error;
      }
    );
  }
};

export const setStatus = async (params: {
  id: number | undefined;
  status: actions.order["order"]["status"] | undefined;
}) => {
  const { id, status } = params;

  if (!id) {
    throw new Error("No id passed");
  }

  if (!status) {
    throw new Error("No status passed");
  }

  return await Fetch(`${url.orderDetails}/${id}/set-status`, {
    method: "PUT",
    body: JSON.stringify({ status, order_id: id })
  })
    .then((res) => {
      const response = res.json();

      return response;
    })
    .catch((err) => {
      throw err;
    });
};

export const setOngoing = async (params: { id: number | undefined }) => {
  return await setStatus({ status: "ongoing", id: params.id });
};

export const reserve = async (params: { id?: number }) => {
  return await setStatus({ status: "reserved", id: params.id });
};

export const getDepositAmount = async (details: {
  order_id: number | undefined;
  cleaner_id: number | undefined;
  services: { id: number; quantity: number }[];
  extra_services: { id: number; parent_id: number; quantity: number }[];
  gift_values: gift_value[];
  coupon_code: string | null;
  full_payment: boolean;
  random_cleaner: boolean;
}) => {
  const { extra_services, services, gift_values } = details;
  const onlyGift =
    extra_services.length + services.length === 0 && gift_values.length;
  const willReturnEarly = !details.order_id || (!onlyGift && !details.cleaner_id && !details.random_cleaner);

  if (willReturnEarly) {
    return;
  }
  return await Fetch(url.getDepositAmount, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(details)
  }).then(
    (res) => {
      const result: Promise<number> = res.json();
      return result;
    },
    (error) => {
      throw error;
    }
  );
};
